import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, Eye, EyeOff, CheckCircle2, AlertCircle, Loader2, Wifi, WifiOff } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const inputCls = "w-full bg-background border border-border rounded-xl px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-ring transition-colors";

const RZP_KEYS = ["razorpay_x_key_id", "razorpay_x_key_secret", "razorpay_x_account_number"];
const RZP_LABELS = {
  razorpay_x_key_id:        "Razorpay X Key ID",
  razorpay_x_key_secret:    "Razorpay X Key Secret",
  razorpay_x_account_number:"Razorpay X Account Number",
};
const RZP_PLACEHOLDERS = {
  razorpay_x_key_id:        "rzp_live_xxxxxxxxxxxxxxxx",
  razorpay_x_key_secret:    "••••••••••••••••••••",
  razorpay_x_account_number:"Your current account number (e.g. 1234567890)",
};

const PERFIOS_KEYS = [
  "perfios_base_url", "perfios_username", "perfios_password",
  "perfios_org_id", "perfios_pan_path", "perfios_bank_path",
];
const PERFIOS_LABELS = {
  perfios_base_url:   "Perfios Base URL",
  perfios_username:   "Username (x-secure-id)",
  perfios_password:   "Password (x-secure-cred)",
  perfios_org_id:     "Organisation ID (x-organization-ID)",
  perfios_pan_path:   "PAN Verification Path",
  perfios_bank_path:  "Bank Verification Path",
};
const PERFIOS_PLACEHOLDERS = {
  perfios_base_url:  "https://hub-test.perfios.ai",
  perfios_username:  "Your Perfios username",
  perfios_password:  "Your Perfios password",
  perfios_org_id:    "Your organisation ID from Perfios Hub",
  perfios_pan_path:  "/ssp/kyc/api/v2/pan",
  perfios_bank_path: "/ssp/kyc/api/v2/bankacc",
};

function SecretField({ label, fieldKey, value, onChange, placeholder }) {
  const [show, setShow] = useState(false);
  return (
    <div className="space-y-1.5">
      <label className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{label}</label>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value || ""}
          onChange={e => onChange(fieldKey, e.target.value)}
          placeholder={placeholder}
          className={`${inputCls} pr-10`}
          data-testid={`rzp-${fieldKey}`}
          autoComplete="off"
        />
        <button type="button" onClick={() => setShow(v => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

export default function SMSettings() {
  const [config, setConfig] = useState({
    module_name: "Sales & Marketing",
    default_currency: "INR",
    default_commission_pct: 10,
    lead_auto_assign: false,
    notify_on_new_lead: true,
    notify_on_deal_close: true,
  });
  const [rzp, setRzp] = useState({ razorpay_x_key_id: "", razorpay_x_key_secret: "", razorpay_x_account_number: "" });
  const [rzpSaved, setRzpSaved] = useState(false);
  const [savingRzp, setSavingRzp] = useState(false);
  const [saving, setSaving] = useState(false);

  // Perfios state
  const [perfios, setPerfios] = useState({
    perfios_enabled: false,
    perfios_base_url: "https://hub-test.perfios.ai",
    perfios_username: "",
    perfios_password: "",
    perfios_org_id: "",
    perfios_pan_path: "/ssp/kyc/api/v2/pan",
    perfios_bank_path: "/ssp/kyc/api/v2/bankacc",
  });
  const [kycAutoApprove, setKycAutoApprove] = useState(true);
  const [perfiosSaved, setPerfiosSaved] = useState(false);
  const [savingPerfios, setSavingPerfios] = useState(false);
  const [testingConn, setTestingConn] = useState(false);
  const [connResult, setConnResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/sm/admin/config`, { withCredentials: true })
      .then(r => setConfig(prev => ({ ...prev, ...r.data })))
      .catch(console.error);
    axios.get(`${API}/api/sm/commission-config/global`, { withCredentials: true })
      .then(r => {
        const cfg = r.data.config || {};
        setRzp({
          razorpay_x_key_id:        cfg.razorpay_x_key_id        || "",
          razorpay_x_key_secret:    cfg.razorpay_x_key_secret     || "",
          razorpay_x_account_number:cfg.razorpay_x_account_number || "",
        });
        setRzpSaved(!!(cfg.razorpay_x_key_id && cfg.razorpay_x_key_secret && cfg.razorpay_x_account_number));
        setPerfios({
          perfios_enabled:   !!cfg.perfios_enabled,
          perfios_base_url:  cfg.perfios_base_url  || "https://hub-test.perfios.ai",
          perfios_username:  cfg.perfios_username  || "",
          perfios_password:  cfg.perfios_password  || "",
          perfios_org_id:    cfg.perfios_org_id    || "",
          perfios_pan_path:  cfg.perfios_pan_path  || "/ssp/kyc/api/v2/pan",
          perfios_bank_path: cfg.perfios_bank_path || "/ssp/kyc/api/v2/bankacc",
        });
        setPerfiosSaved(!!(cfg.perfios_username && cfg.perfios_password && cfg.perfios_org_id));
        setKycAutoApprove(cfg.kyc_auto_approve_enabled !== false); // default true
      })
      .catch(console.error);
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/api/sm/admin/config`, config, { withCredentials: true });
      toast.success("Settings saved");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleRzpChange = (key, val) => setRzp(p => ({ ...p, [key]: val }));

  const handleSaveRzp = async () => {
    if (!rzp.razorpay_x_key_id || !rzp.razorpay_x_key_secret) {
      toast.error("Key ID and Key Secret are required"); return;
    }
    setSavingRzp(true);
    try {
      for (const key of RZP_KEYS) {
        await axios.patch(`${API}/api/sm/commission-config/global`,
          { key, value: rzp[key] }, { withCredentials: true });
      }
      setRzpSaved(true);
      toast.success("Razorpay X credentials saved");
    } catch {
      toast.error("Failed to save Razorpay X credentials");
    } finally { setSavingRzp(false); }
  };

  const handlePerfiosChange = (key, val) => setPerfios(p => ({ ...p, [key]: val }));

  const handleSavePerfios = async () => {
    if (!perfios.perfios_username || !perfios.perfios_password || !perfios.perfios_org_id) {
      toast.error("Username, Password and Organisation ID are required"); return;
    }
    setSavingPerfios(true);
    try {
      for (const key of PERFIOS_KEYS) {
        await axios.patch(`${API}/api/sm/commission-config/global`,
          { key, value: perfios[key] }, { withCredentials: true });
      }
      // Save enabled toggle + auto-approve toggle
      await axios.patch(`${API}/api/sm/commission-config/global`,
        { key: "perfios_enabled", value: perfios.perfios_enabled }, { withCredentials: true });
      await axios.patch(`${API}/api/sm/commission-config/global`,
        { key: "kyc_auto_approve_enabled", value: kycAutoApprove }, { withCredentials: true });
      setPerfiosSaved(true);
      setConnResult(null);
      toast.success("Perfios KYC credentials saved");
    } catch {
      toast.error("Failed to save Perfios credentials");
    } finally { setSavingPerfios(false); }
  };

  const handleTestConnection = async () => {
    setTestingConn(true);
    setConnResult(null);
    try {
      const r = await axios.post(`${API}/api/sm/kyc/test-connection`, {}, { withCredentials: true });
      setConnResult(r.data);
      if (r.data.ok) toast.success("Connection test passed");
      else toast.error(r.data.message || "Connection test failed");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Connection test failed";
      setConnResult({ ok: false, message: msg });
      toast.error(msg);
    } finally { setTestingConn(false); }
  };

  return (
    <div className="p-4 sm:p-6 space-y-8 max-w-2xl">
      <div>
        <div className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Configuration</div>
        <h1 className="text-2xl font-bold text-foreground">Settings</h1>
        <p className="text-muted-foreground text-sm mt-1">Configure the Partner & Commission module</p>
      </div>

      {/* General */}
      <div className="bg-card border border-border rounded-2xl divide-y divide-border">
        <div className="p-5 space-y-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-medium">General</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Module Name</label>
              <input value={config.module_name || ""} onChange={e => setConfig({ ...config, module_name: e.target.value })} className={inputCls} data-testid="sm-module-name" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Default Currency</label>
              <select value={config.default_currency || "INR"} onChange={e => setConfig({ ...config, default_currency: e.target.value })} className={inputCls}>
                <option value="INR">INR — Indian Rupee</option>
                <option value="USD">USD — US Dollar</option>
              </select>
            </div>
          </div>
        </div>

        {/* Behaviour toggles */}
        <div className="p-5 space-y-4">
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-medium">Behaviour</p>
          {[
            { key: "lead_auto_assign", label: "Auto-assign new leads", desc: "Round-robin assignment to available team members" },
            { key: "notify_on_new_lead", label: "Notify on new lead", desc: "Send notification when a new lead is created" },
            { key: "notify_on_deal_close", label: "Notify on deal close", desc: "Send notification when a deal is won or lost" },
          ].map(item => (
            <div key={item.key} className="flex items-start gap-4">
              <button
                onClick={() => setConfig({ ...config, [item.key]: !config[item.key] })}
                className={`mt-0.5 w-9 h-5 rounded-full transition-colors shrink-0 relative ${config[item.key] ? "bg-primary" : "bg-border"}`}
                role="switch" aria-checked={config[item.key]}
              >
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${config[item.key] ? "left-4" : "left-0.5"}`} />
              </button>
              <div>
                <p className="text-sm text-foreground font-medium">{item.label}</p>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <button onClick={handleSave} disabled={saving} data-testid="sm-settings-save"
        className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium rounded-xl hover:opacity-90 disabled:opacity-60 transition-colors">
        <Save size={14} />
        {saving ? "Saving…" : "Save Settings"}
      </button>

      {/* ── Razorpay X Configuration ──────────────────────────── */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="razorpay-config-panel">
        <div className="p-5 border-b border-border flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-medium mb-0.5">Razorpay X Payout Configuration</p>
            <p className="text-[12px] text-muted-foreground">Credentials for automated bulk payouts to partner bank accounts</p>
          </div>
          {rzpSaved
            ? <span className="self-start sm:self-center flex items-center gap-1.5 text-[11px] text-emerald-600 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2.5 py-1">
                <CheckCircle2 className="w-3 h-3" />Configured
              </span>
            : <span className="self-start sm:self-center flex items-center gap-1.5 text-[11px] text-amber-600 bg-amber-500/10 border border-amber-500/20 rounded-full px-2.5 py-1">
                <AlertCircle className="w-3 h-3" />Not configured
              </span>
          }
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-start gap-3 bg-primary/5 border border-primary/15 rounded-xl p-3.5 text-[11px] text-muted-foreground">
            <AlertCircle className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
            <span>
              These credentials are stored securely in the database. Obtain them from your{" "}
              <a href="https://x.razorpay.com" target="_blank" rel="noreferrer" className="text-primary underline">Razorpay X Dashboard</a>.
              Required for Monthly Statements → Payout initiation.
            </span>
          </div>

          <SecretField label={RZP_LABELS.razorpay_x_key_id}        fieldKey="razorpay_x_key_id"        value={rzp.razorpay_x_key_id}        onChange={handleRzpChange} placeholder={RZP_PLACEHOLDERS.razorpay_x_key_id} />
          <SecretField label={RZP_LABELS.razorpay_x_key_secret}     fieldKey="razorpay_x_key_secret"     value={rzp.razorpay_x_key_secret}     onChange={handleRzpChange} placeholder={RZP_PLACEHOLDERS.razorpay_x_key_secret} />

          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{RZP_LABELS.razorpay_x_account_number}</label>
            <input
              type="text"
              value={rzp.razorpay_x_account_number || ""}
              onChange={e => handleRzpChange("razorpay_x_account_number", e.target.value)}
              placeholder={RZP_PLACEHOLDERS.razorpay_x_account_number}
              className={inputCls}
              data-testid="rzp-razorpay_x_account_number"
            />
            <p className="text-[11px] text-muted-foreground">Your Razorpay X current account number (source account for payouts)</p>
          </div>

          <button onClick={handleSaveRzp} disabled={savingRzp} data-testid="save-rzp-config-btn"
            className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground text-[13px] font-medium rounded-xl hover:opacity-90 disabled:opacity-60 transition-colors">
            <Save size={13} />
            {savingRzp ? "Saving…" : "Save Razorpay X Credentials"}
          </button>
        </div>
      </div>

      {/* ── Perfios KYC Configuration ─────────────────────────────── */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="perfios-config-panel">
        <div className="p-5 border-b border-border flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-medium mb-0.5">Perfios KYC Configuration</p>
            <p className="text-[12px] text-muted-foreground">Live PAN &amp; Bank Account Verification via Perfios Hub</p>
          </div>
          {perfios.perfios_enabled && perfiosSaved
            ? <span className="self-start sm:self-center flex items-center gap-1.5 text-[11px] text-emerald-600 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-2.5 py-1">
                <CheckCircle2 className="w-3 h-3" />Live Mode
              </span>
            : <span className="self-start sm:self-center flex items-center gap-1.5 text-[11px] text-amber-600 bg-amber-500/10 border border-amber-500/20 rounded-full px-2.5 py-1">
                <AlertCircle className="w-3 h-3" />Mock Mode
              </span>
          }
        </div>

        <div className="p-5 space-y-4">
          {/* Info banner */}
          <div className="flex items-start gap-3 bg-primary/5 border border-primary/15 rounded-xl p-3.5 text-[11px] text-muted-foreground">
            <AlertCircle className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" />
            <span>
              Obtain your credentials from the{" "}
              <a href="https://hub.perfios.ai" target="_blank" rel="noreferrer" className="text-primary underline">Perfios Hub Dashboard</a>.
              Sandbox: <code className="font-mono">hub-test.perfios.ai</code>. Check your sandbox documentation for the exact API endpoint paths.
              When disabled, the platform uses mock verification (format checks only).
            </span>
          </div>

          {/* Enable toggle */}
          <div className="flex items-center gap-3 p-3 border border-border rounded-xl">
            <button
              onClick={() => setPerfios(p => ({ ...p, perfios_enabled: !p.perfios_enabled }))}
              className={`w-9 h-5 rounded-full transition-colors shrink-0 relative ${perfios.perfios_enabled ? "bg-primary" : "bg-border"}`}
              role="switch" aria-checked={perfios.perfios_enabled} data-testid="perfios-enabled-toggle"
            >
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${perfios.perfios_enabled ? "left-4" : "left-0.5"}`} />
            </button>
            <div>
              <p className="text-sm text-foreground font-medium">Enable Live Verification</p>
              <p className="text-xs text-muted-foreground">When enabled, all PAN and Bank verifications will call Perfios APIs</p>
            </div>
          </div>

          {/* Auto-Approve toggle */}
          <div className="flex items-center gap-3 p-3 border border-border rounded-xl">
            <button
              onClick={() => setKycAutoApprove(v => !v)}
              className={`w-9 h-5 rounded-full transition-colors shrink-0 relative ${kycAutoApprove ? "bg-primary" : "bg-border"}`}
              role="switch" aria-checked={kycAutoApprove} data-testid="kyc-auto-approve-toggle"
            >
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${kycAutoApprove ? "left-4" : "left-0.5"}`} />
            </button>
            <div>
              <p className="text-sm text-foreground font-medium">KYC Auto-Approval</p>
              <p className="text-xs text-muted-foreground">
                When PAN <strong>+</strong> Bank both verified <strong>with name match</strong>, partner is auto-activated — no admin click required
              </p>
            </div>
          </div>

          {/* Base URL */}
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{PERFIOS_LABELS.perfios_base_url}</label>
            <input
              type="text"
              value={perfios.perfios_base_url}
              onChange={e => handlePerfiosChange("perfios_base_url", e.target.value)}
              placeholder={PERFIOS_PLACEHOLDERS.perfios_base_url}
              className={inputCls}
              data-testid="perfios-base-url"
            />
            <p className="text-[11px] text-muted-foreground">Use <code className="font-mono">hub-test.perfios.ai</code> for sandbox, <code className="font-mono">hub.perfios.ai</code> for production</p>
          </div>

          {/* Secret fields */}
          <SecretField label={PERFIOS_LABELS.perfios_username} fieldKey="perfios_username" value={perfios.perfios_username} onChange={handlePerfiosChange} placeholder={PERFIOS_PLACEHOLDERS.perfios_username} />
          <SecretField label={PERFIOS_LABELS.perfios_password} fieldKey="perfios_password" value={perfios.perfios_password} onChange={handlePerfiosChange} placeholder={PERFIOS_PLACEHOLDERS.perfios_password} />

          {/* Org ID */}
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{PERFIOS_LABELS.perfios_org_id}</label>
            <input
              type="text"
              value={perfios.perfios_org_id}
              onChange={e => handlePerfiosChange("perfios_org_id", e.target.value)}
              placeholder={PERFIOS_PLACEHOLDERS.perfios_org_id}
              className={inputCls}
              data-testid="perfios-org-id"
            />
          </div>

          {/* API Paths */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{PERFIOS_LABELS.perfios_pan_path}</label>
              <input
                type="text"
                value={perfios.perfios_pan_path}
                onChange={e => handlePerfiosChange("perfios_pan_path", e.target.value)}
                placeholder={PERFIOS_PLACEHOLDERS.perfios_pan_path}
                className={`${inputCls} font-mono text-[12px]`}
                data-testid="perfios-pan-path"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground uppercase tracking-wider font-medium">{PERFIOS_LABELS.perfios_bank_path}</label>
              <input
                type="text"
                value={perfios.perfios_bank_path}
                onChange={e => handlePerfiosChange("perfios_bank_path", e.target.value)}
                placeholder={PERFIOS_PLACEHOLDERS.perfios_bank_path}
                className={`${inputCls} font-mono text-[12px]`}
                data-testid="perfios-bank-path"
              />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground -mt-2">
            Check your Perfios Hub sandbox documentation for exact endpoint paths. The full URL = Base URL + Path.
          </p>

          {/* Connection test result */}
          {connResult && (
            <div className={`flex items-start gap-2.5 px-3.5 py-3 rounded-xl text-[12px] border ${connResult.ok ? "bg-emerald-500/5 border-emerald-500/20 text-emerald-700" : "bg-red-500/5 border-red-500/20 text-red-700"}`}
              data-testid="perfios-conn-result">
              {connResult.ok ? <Wifi className="w-4 h-4 mt-0.5 shrink-0" /> : <WifiOff className="w-4 h-4 mt-0.5 shrink-0" />}
              <span>{connResult.message}</span>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-wrap gap-3 pt-1">
            <button onClick={handleSavePerfios} disabled={savingPerfios} data-testid="save-perfios-config-btn"
              className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground text-[13px] font-medium rounded-xl hover:opacity-90 disabled:opacity-60 transition-colors">
              {savingPerfios ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
              {savingPerfios ? "Saving…" : "Save Perfios Credentials"}
            </button>
            <button onClick={handleTestConnection} disabled={testingConn || !perfios.perfios_username} data-testid="test-perfios-connection-btn"
              className="flex items-center gap-2 px-4 py-2.5 border border-border text-foreground text-[13px] font-medium rounded-xl hover:bg-muted/50 disabled:opacity-60 transition-colors">
              {testingConn ? <Loader2 size={13} className="animate-spin" /> : <Wifi size={13} />}
              {testingConn ? "Testing…" : "Test Connection"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
